import { ConsumptionType, ContentAuthorizationData, PlatformAsset, PlaybackMode } from 'fl-content-authorization';
import { PlatformAuthorizer } from 'fl-platform-authorizer';
import { getLicenseHandler } from 'fl-platform-player';
import { PlayerBuilder, createComposablePlayer, createPlayerBuilder } from 'fl-player';
import {
  ComposablePlayer,
  DrmScheme,
  MediaType,
  PlaybackOptions,
  Player,
  RequestType,
  TrackType,
} from 'fl-player-interface';
import { DeviceTypeModel, getDeviceManufacturer, getDeviceType } from 'qp-utility';
import { GetPlatformPlayerModel } from '../interfaces/player.interface';
import { LocalStorageKeys } from '../LocalStorageKeys';
import { AppConfig } from '../services/config/config.service';
import { LocalStorageService } from '../services/local-storage/local-storage.service';

declare const window: Window &
  typeof globalThis & {
    env: any;
    deviceModel: any;
    showDotAnimation: any;
    xSupportedResolution: any;
    xSupportedAudio: any;
    doNotTrack: any;
  };

declare let navigator: any;

export const getQualityPreference = (localStorage: LocalStorageService) => {
  return localStorage.getItem(LocalStorageKeys.QUALITY_PREFERENCE) ? localStorage.getItem(LocalStorageKeys.QUALITY_PREFERENCE) : 'Auto';
};

export const getSubtitlesPreference = (localStorage: LocalStorageService) => {
  return localStorage.getItem(LocalStorageKeys.SUBTITLES_LANGUAGE) || 'off';
};

const getCatalogType = (metadataType: string): string => {
  switch (metadataType) {
    case 'webseries':
    case 'webepisode':
      return 'webepisode';
    default:
      return metadataType;
  }
};

export const getConsumptionType = (metadataType: string): ConsumptionType => {
  switch (metadataType) {
    case 'channel':
    case 'livechannel':
    case 'liveevent':
      return ConsumptionType.LIVE;
    default:
      return ConsumptionType.VOD;
  }
};

const getPlaybackMode = (metadataType: string): PlaybackMode | undefined => {
  switch (metadataType) {
    case 'channel':
    case 'livechannel':
    case 'liveevent':
      return PlaybackMode.LIVE;
    default:
      return undefined;
  }
};

export const getHeaders = (): any => {
  const X_SUPPORTED_RESOLUTION = 'X-supported-resolution';
  const X_SUPPORTED_AUDIO = 'X-supported-audio';
  const headers = {
    [X_SUPPORTED_RESOLUTION]: window.xSupportedResolution,
    [X_SUPPORTED_AUDIO]: window.xSupportedAudio?.join(','),
  };
  return headers;
};

export const getPlatformAsset = (contentId: string, metadataType: string): PlatformAsset => {
  let model = '';
  if (navigator.userAgent.includes('Tizen')) {
    const arr = navigator.userAgent.split(' ');
    model = arr[1] + ' ' + arr[2] + ' ' + arr[3] + ' ' + arr[4] + ' ';
  } else if (navigator.userAgent.includes('Web0S')) {
    const arr = navigator.userAgent.split(' ');
    model = arr[1] + ' ' + arr[2];
  }
  const platformAsset: PlatformAsset = {
    mediaID: contentId,
    consumptionType: getConsumptionType(metadataType),
    catalogType: getCatalogType(metadataType),
    mediaType: MediaType.HLS,
    drmScheme: getDrmScheme(),
    deviceManufacturer: getDeviceManufacturer(),
    deviceOs: navigator.userAgent,
    deviceModelName: window.deviceModel,
    deviceModelNumber: model.trim(),
    supportedResolution: window.xSupportedResolution,
    supportedAudio: window.xSupportedAudio?.join(','),
  };

  const playbackMode = getPlaybackMode(metadataType);
  if (playbackMode) {
    platformAsset.playbackMode = playbackMode;
  }

  return platformAsset;
};

export const getDrmScheme = (): DrmScheme => {
  return DrmScheme.NONE;
};

const getBufferConfig = (applicationConfig: AppConfig) => {
  const playbackBufferOptions: Pick<PlaybackOptions, 'minBuffer' | 'maxBuffer' | 'bufferBehind' | 'ignoreMinBufferTime'> = {};

  if (applicationConfig.minBuffer) {
    playbackBufferOptions.minBuffer = parseFloat(applicationConfig.minBuffer);
  }

  if (applicationConfig.maxBuffer) {
    playbackBufferOptions.maxBuffer = parseFloat(applicationConfig.maxBuffer);
  }

  if (applicationConfig.bufferBehind) {
    playbackBufferOptions.bufferBehind = parseFloat(applicationConfig.bufferBehind);
  }

  if (applicationConfig.ignoreMinBufferTime) {
    playbackBufferOptions.ignoreMinBufferTime = applicationConfig.ignoreMinBufferTime === 'true';
  }

  return playbackBufferOptions;
};

const getNetworkConfig = (applicationConfig: AppConfig) => {
  const playbackNetworkConfig: Pick<PlaybackOptions, 'networkRetries' | 'networkTimeout' | 'connectionTimeout' | 'useNetworkInformation'> =
    {};

  if (applicationConfig.networkRetries) {
    playbackNetworkConfig.networkRetries = parseFloat(applicationConfig.networkRetries);
  }

  if (applicationConfig.networkTimeout) {
    playbackNetworkConfig.networkTimeout = parseFloat(applicationConfig.networkTimeout);
  }

  if (applicationConfig.connectionTimeout) {
    playbackNetworkConfig.connectionTimeout = parseFloat(applicationConfig.connectionTimeout);
  }

  if (typeof applicationConfig.useNetworkInformation === 'boolean') {
    playbackNetworkConfig.useNetworkInformation = applicationConfig.useNetworkInformation;
  }

  return playbackNetworkConfig;
};

const getVodPlaybackOptions = (applicationConfig: AppConfig): PlaybackOptions => {
  const playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    ...getBufferConfig(applicationConfig),
    ...getNetworkConfig(applicationConfig),
  };

  if (applicationConfig.stallTimeout) {
    playbackOptions.stallTimeout = parseFloat(applicationConfig.stallTimeout);
  }

  if (applicationConfig.connectionTimeout) {
    playbackOptions.connectionTimeout = parseFloat(applicationConfig.connectionTimeout);
  }

  if (applicationConfig.retryDelay) {
    playbackOptions.retryDelay = parseFloat(applicationConfig.retryDelay);
  }

  if (applicationConfig.preferredBitrate) {
    playbackOptions.preferredBitrate = parseFloat(applicationConfig.preferredBitrate);
  }

  if (applicationConfig.inaccurateManifestTolerance) {
    playbackOptions.inaccurateManifestTolerance = parseFloat(applicationConfig.inaccurateManifestTolerance);
  }

  if (applicationConfig.ignoreSuggestedPresentationDelay && typeof applicationConfig.ignoreSuggestedPresentationDelay === 'boolean') {
    playbackOptions.ignoreSuggestedPresentationDelay = applicationConfig.ignoreSuggestedPresentationDelay;
  }

  if (applicationConfig.defaultPresentationDelay) {
    playbackOptions.defaultPresentationDelay = parseFloat(applicationConfig.defaultPresentationDelay);
  }

  if (applicationConfig.liveMinTotalBytes) {
    playbackOptions.minTotalBytes = parseFloat(applicationConfig.liveMinTotalBytes);
  }

  if (applicationConfig.minBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.minBytes);
  }

  if (applicationConfig.fastHalfLife) {
    playbackOptions.fastHalfLife = parseFloat(applicationConfig.fastHalfLife);
  }

  if (applicationConfig.slowHalfLife) {
    playbackOptions.slowHalfLife = parseFloat(applicationConfig.slowHalfLife);
  }

  if (applicationConfig.bandwidthUpgradeTarget) {
    playbackOptions.bandwidthUpgradeTarget = parseFloat(applicationConfig.bandwidthUpgradeTarget);
  }

  if (applicationConfig.bandwidthDowngradeTarget) {
    playbackOptions.bandwidthDowngradeTarget = parseFloat(applicationConfig.bandwidthDowngradeTarget);
  }

  if (applicationConfig.switchInterval) {
    playbackOptions.switchInterval = parseFloat(applicationConfig.switchInterval);
  }

  return playbackOptions;
};

const getLiveBufferOptionsConfig = (applicationConfig: AppConfig) => {
  const playbackBufferOptions: Pick<PlaybackOptions, 'minBuffer' | 'maxBuffer' | 'bufferBehind' | 'ignoreMinBufferTime'> = {};

  if (applicationConfig.liveMinBuffer) {
    playbackBufferOptions.minBuffer = parseFloat(applicationConfig.liveMinBuffer);
  }

  if (applicationConfig.liveMaxBuffer) {
    playbackBufferOptions.maxBuffer = parseFloat(applicationConfig.liveMaxBuffer);
  }

  if (applicationConfig.liveBufferBehind) {
    playbackBufferOptions.bufferBehind = parseFloat(applicationConfig.liveBufferBehind);
  }

  if (applicationConfig.liveIgnoreMinBufferTime) {
    playbackBufferOptions.ignoreMinBufferTime = applicationConfig.liveIgnoreMinBufferTime === 'true';
  }
  return playbackBufferOptions;
};

const getLiveNetworkConfig = (applicationConfig: AppConfig) => {
  const playbackNetworkConfig: Pick<PlaybackOptions, 'networkRetries' | 'networkTimeout' | 'connectionTimeout' | 'useNetworkInformation'> =
    {};

  if (applicationConfig.liveNetworkRetries) {
    playbackNetworkConfig.networkRetries = parseFloat(applicationConfig.liveNetworkRetries);
  }

  if (applicationConfig.liveNetworkTimeout) {
    playbackNetworkConfig.networkTimeout = parseFloat(applicationConfig.liveNetworkTimeout);
  }

  if (applicationConfig.liveConnectionTimeout) {
    playbackNetworkConfig.connectionTimeout = parseFloat(applicationConfig.liveConnectionTimeout);
  }

  if (typeof applicationConfig.liveUseNetworkInformation === 'boolean') {
    playbackNetworkConfig.useNetworkInformation = applicationConfig.liveUseNetworkInformation;
  }
  return playbackNetworkConfig;
};

const getLivePlaybackOptions = (applicationConfig: AppConfig): PlaybackOptions => {
  const playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    ...getLiveBufferOptionsConfig(applicationConfig),
    ...getLiveNetworkConfig(applicationConfig),
  };

  if (applicationConfig.liveStallTimeout) {
    playbackOptions.stallTimeout = parseFloat(applicationConfig.liveStallTimeout);
  }

  if (applicationConfig.liveRetryDelay) {
    playbackOptions.retryDelay = parseFloat(applicationConfig.liveRetryDelay);
  }

  if (applicationConfig.livePreferredBitrate) {
    playbackOptions.preferredBitrate = parseFloat(applicationConfig.livePreferredBitrate);
  }

  if (applicationConfig.liveInaccurateManifestTolerance) {
    playbackOptions.inaccurateManifestTolerance = parseFloat(applicationConfig.liveInaccurateManifestTolerance);
  }

  if (applicationConfig.liveIgnoreSuggestedPresentationDelay && typeof applicationConfig.ignoreSuggestedPresentationDelay === 'boolean') {
    playbackOptions.ignoreSuggestedPresentationDelay = applicationConfig.liveIgnoreSuggestedPresentationDelay;
  }

  if (applicationConfig.liveDefaultPresentationDelay) {
    playbackOptions.defaultPresentationDelay = parseFloat(applicationConfig.liveDefaultPresentationDelay);
  }

  if (applicationConfig.liveMinTotalBytes) {
    playbackOptions.minTotalBytes = parseFloat(applicationConfig.liveMinTotalBytes);
  }

  if (applicationConfig.liveMinBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.liveMinBytes);
  }

  if (applicationConfig.liveFastHalfLife) {
    playbackOptions.fastHalfLife = parseFloat(applicationConfig.liveFastHalfLife);
  }

  if (applicationConfig.liveSlowHalfLife) {
    playbackOptions.slowHalfLife = parseFloat(applicationConfig.liveSlowHalfLife);
  }

  if (applicationConfig.liveBandwidthUpgradeTarget) {
    playbackOptions.bandwidthUpgradeTarget = parseFloat(applicationConfig.liveBandwidthUpgradeTarget);
  }

  if (applicationConfig.liveBandwidthDowngradeTarget) {
    playbackOptions.bandwidthDowngradeTarget = parseFloat(applicationConfig.liveBandwidthDowngradeTarget);
  }

  if (applicationConfig.liveSwitchInterval) {
    playbackOptions.switchInterval = parseFloat(applicationConfig.liveSwitchInterval);
  }
  return playbackOptions;
};

export const getPlaybackOptions = (applicationConfig: AppConfig, initialPlaybackTime = 0, isLive = false): PlaybackOptions => {
  const navigatorAgent = navigator.userAgent;
  const chrome = navigatorAgent.substr(navigatorAgent.indexOf('Chrome/'), navigatorAgent.indexOf('.')).split('/');
  let playbackOptions;
  if (!isLive) {
    playbackOptions = getVodPlaybackOptions(applicationConfig);
  } else if (isLive) {
    playbackOptions = getLivePlaybackOptions(applicationConfig);
    return playbackOptions;
  }
  playbackOptions.initialPlaybackTime = initialPlaybackTime;

  if (parseInt(chrome[1], 10) < 40 || (!navigatorAgent.includes('Web0S') && !navigatorAgent.includes('Tizen'))) {
    // For stop buffering
    playbackOptions.minBuffer = 2;
    playbackOptions.maxBuffer = 3;
    playbackOptions.bufferBehind = 2;

    return playbackOptions;
  }

  return playbackOptions;
};

export const getTextTrackList = (playerInstance: Player): any[] => {
  let textTrackList = [];
  if (playerInstance) {
    const trackList = playerInstance.getAllTracks();
    const activeTracks = playerInstance.getActiveTracks();

    textTrackList = trackList
      .filter((t) => t.type === TrackType.TEXT)
      .map((t) => ({
        type: t.type,
        language: t.language,
        active:
          activeTracks.filter((a) => a.type === TrackType.TEXT).length > 0
            ? activeTracks.filter((a) => a.type === TrackType.TEXT)[0].language === t.language
            : false,
      }));

    const offTextTrack = {
      type: TrackType.TEXT,
      language: 'off',
      name: 'Off',
      active: textTrackList.filter((t) => t.active).length <= 0,
    };

    textTrackList.unshift(offTextTrack);
  }
  return textTrackList;
};

export const getAudioTrackList = (playerInstance: Player): any[] => {
  let audioTrackList = [];
  if (playerInstance) {
    const trackList = playerInstance.getAllTracks();
    const activeTracks = playerInstance.getActiveTracks();

    audioTrackList = trackList
      .filter((t) => t.type === TrackType.AUDIO)
      .map((t) => ({
        type: t.type,
        language: t.language,
        active:
          activeTracks.filter((a) => a.type === TrackType.AUDIO).length > 0
            ? activeTracks.filter((a) => a.type === TrackType.AUDIO)[0].language === t.language
            : false,
      }));
    audioTrackList.unshift();
  }
  return audioTrackList;
};

export const getComposablePlayer = (playerInstance: Player): ComposablePlayer => {
  return createComposablePlayer(playerInstance);
};

const buildPlayer = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  showThumbnails: boolean,
  thumbnailsUrl?: string
): Player => {
  const playerBuilder: PlayerBuilder = createPlayerBuilder()
    .mediaElement(video)
    .mediaUrl(contentAuthorizationData.contentUrl)
    .playbackOptions(playbackOptions)
    .drmLicenseUrl(contentAuthorizationData.drmLicenseUrl)
    .drmScheme(contentAuthorizationData.drmScheme)
    .mediaType(contentAuthorizationData.mediaType);

  if (showThumbnails && thumbnailsUrl) {
    playerBuilder.webVTTThumbnailPreview(thumbnailsUrl);
  }

  return playerBuilder.build();
};

const getPlayer = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  showThumbnails: boolean,
  thumbnailsUrl?: string
): Promise<Player> => {
  try {
    return Promise.resolve(buildPlayer(playbackOptions, contentAuthorizationData, video, showThumbnails, thumbnailsUrl));
  } catch (_) {
    return Promise.resolve(buildPlayer(playbackOptions, contentAuthorizationData, video, false));
  }
};

export const getPlatformPlayer = (getPlatformPlayerModel: GetPlatformPlayerModel): Promise<Player> => {
  const { seekPos, applicationConfig, isLive, contentAuthorizationData, video, showThumbnails, thumbnailsUrl } = getPlatformPlayerModel;
  const playbackOptions: PlaybackOptions = getPlaybackOptions(applicationConfig, seekPos, isLive);
  if (getDeviceType() === DeviceTypeModel.VIZIO) {
    if (applicationConfig.vizioPlaybackOptimization) {
      playbackOptions.safariBehaviourForLiveHls = applicationConfig.safariBehaviourForLiveHlsVizio;
      playbackOptions.bufferTimeoutMs = applicationConfig.bufferTimeoutMsVizio;
    }
    playbackOptions.forceTransmuxTS = true;
  }
  try {
    const navigatorAgent = navigator.userAgent;
    const chromeVersion = parseInt(navigatorAgent.match(/Chrome\/(\d+)/)?.[1] || '0', 10);

    if (chromeVersion < 40 || (!navigatorAgent.includes('Web0S') && !navigatorAgent.includes('Tizen'))) {
      return getPlayer(playbackOptions, contentAuthorizationData, video, showThumbnails, thumbnailsUrl);
    }
    return getPlayer(playbackOptions, contentAuthorizationData, video, showThumbnails, thumbnailsUrl);
  } catch (e) {
    return getPlayer(playbackOptions, contentAuthorizationData, video, false);
  }
};

export const handleLicenseRequest = async (platformAuthorizer: PlatformAuthorizer, playerInstance: Player): Promise<Player> => {
  /**
   * Get the default license handler from FLPlatformPlayer
   * and set those as request/response interceptors on the player instance.
   *
   * The license handler injects the authorization tokens required for
   * DRM proxy server.
   */
  const licenseHandler = await getLicenseHandler(platformAuthorizer);
  const requestInterceptor = async (type, request) => {
    if (type === RequestType.LICENSE) {
      licenseHandler.licenseRequestHandler(request);
    }
  };

  playerInstance.setRequestInterceptor(requestInterceptor);
  return playerInstance;
};

/**
 * Validate if thumbnails are supported
 * based on the userAgent versions
 *
 * @returns true or false
 */
export const thumbnailsPlatformValidation = (): boolean => {
  const userAgent = navigator.userAgent;
  if (userAgent.toLocaleLowerCase().includes('tizen')) {
    const tizenVersion = navigator.userAgent.slice(30, 39);
    const tizenNum = tizenVersion.split(' ')[1];
    return parseFloat(tizenNum) > 5;
  } else if (userAgent.toLocaleLowerCase().includes('web0s')) {
    const chrome = userAgent.substr(userAgent.indexOf('Chrome/'), userAgent.indexOf('.')).split('/');
    return parseInt(chrome[1], 10) >= 53;
  } else if (userAgent.toLocaleLowerCase().includes('vizio')) {
    const chrome = userAgent.substr(userAgent.indexOf('Chrome/'), userAgent.indexOf('.')).split('/');
    return parseInt(chrome[1], 10) >= 8;
  } else if (window.env === '%APP_ENV%') return true;
  return false;
};

/**
 * Check if playback is enabled by route data
 *
 * @param event
 * @returns
 */
export const isPlaybackEnabledByRoute = (currentUrl: string, tabsUrl: string[]): boolean => {
  const playbackUrls = ['/epg', '/player/', '/collection/'];
  return tabsUrl.some((url) => currentUrl.includes(url)) || playbackUrls.some((url) => currentUrl.includes(url)) || currentUrl === '/';
};